import { useCallback, useEffect, useState } from 'react'
import { Button, Descriptions, Divider, Form, Input, InputNumber, Modal, Select, Space, TableProps } from 'antd'
import { EyeOutlined, PlusOutlined, MinusOutlined, EditOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { toastError, toastSuccess } from '../../utils/toast'
import { useLoading } from '../../context/LoadingContext'
import { Link } from 'react-router-dom'
import { DETAIL, EXPORT, ORDER } from '../../assets/constants/route_path'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { orderStatusOptions } from '../../assets/constants/default_options'
import FormSearch from '../../components/form'
import { apiGetListCompanies, apiGetListOrderInventory, apiAddOrderInventory, apiGetSimInventoryByOrderIds, updateOrderInventory, apiCreateOutletOrder, apiFilterOutlets, apiFilterOutletLinks, apiLinkExistingOutletOrder } from '../../api'
import { ICompany } from '../../models/company.modal'
import { IOutlet } from '../../models/outlet.modal'
import { renderOrderStatus } from '../quanlytopup'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import moment from 'moment'

const initOrderInfo = { cpCode: "", description: "" }
const initSimInventory = { id: 0, startSim: "", endSim: "", disabled: false, prefix_sim: "", start_sim: 0, end_sim: 0, total_sim: 0 }

export default function SimExportOrder() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [orderInventory, setOrderInventory] = useState<any>({})
    const [listSimByOrderIds, setListSimByOrderIds] = useState<any>([])
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({})
    const [modalUpload, setModalUpload] = useState<boolean>(false)
    const [orderInfo, setOrderInfo] = useState(initOrderInfo)
    const [simInventory, setSimInventory] = useState([initSimInventory])
    // If this cpCode has outlets (đại lý), let user attach the order to one.
    // Empty selection = raw addOrderInventory (no link); selected = wrapper
    // POST /outlet-order/create so BFF also creates outlet_inventory_link rows.
    const [outletOptions, setOutletOptions] = useState<{ label: string, value: string }[]>([])
    const [selectedOutletId, setSelectedOutletId] = useState<string | undefined>(undefined)
    // Update modal: existing link detection + post-hoc link picker.
    const [updateOutletOptions, setUpdateOutletOptions] = useState<{ label: string, value: string }[]>([])
    const [updateSelectedOutletId, setUpdateSelectedOutletId] = useState<string | undefined>(undefined)
    const [existingLinkedOutletId, setExistingLinkedOutletId] = useState<string | null>(null)
    const [modalUpdate, setModalUpdate] = useState<{ isOpen: boolean, data: any }>({ isOpen: false, data: null })

    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const [formUpdate] = Form.useForm()
    const { account } = useSelector(authSelector)

    const columns: TableProps<any>['columns'] = [
        {
            key: 'order_id',
            title: 'Mã đơn hàng',
            dataIndex: 'order_id',
            render: order_id => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {order_id}
                </span>
            ),
        },
        {
            key: 'owner_by',
            title: 'Nhân sự tạo',
            dataIndex: 'owner_by',
        },
        {
            key: 'description',
            title: 'Nội dung',
            dataIndex: 'description',
        },
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'totalSim',
            title: 'Tổng số sim',
            dataIndex: 'total_sim',
        },
        {
            key: 'total_success',
            title: 'Tổng thành công',
            dataIndex: 'total_success',
        },
        {
            key: 'total_fail',
            title: 'Tổng thất bại',
            dataIndex: 'total_fail',
        },
        {
            key: 'created_at',
            title: 'Ngày bán',
            dataIndex: 'created_at',
            render: created_at => moment(created_at).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            key: 'simInventory',
            title: 'Đã kích',
            dataIndex: 'simInventory',
            render: simInventory => simInventory?.reduce((acc: number, curr: any) => acc + curr.total_active, 0),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderOrderStatus(status),
        },
        {
            key: 'action',
            title: 'Tác vụ',
            dataIndex: 'action',
            align: 'center',
            render: (_, data: any) => (
                <Space>
                    {account?.canGetListSimInventory && (
                        <Link to={EXPORT + ORDER + DETAIL + `?orderId=${data.order_id}`}>
                            <Button
                                icon={<EyeOutlined />}
                                style={{ background: '#289e45', color: 'white' }}
                            />
                        </Link>
                    )}
                    {account?.canUpdateOrderInventory && (
                        <Button
                            onClick={() => handleOpenModalUpdate(data)}
                            icon={<EditOutlined />}
                            style={{ background: '#ff9800', color: 'white' }}
                        />
                    )}
                </Space>
            ),
        },
    ]

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetListOrderInventory({
                cpCode: search.cpCode,
                status: search.status,
                page: currentPage,
                size: pageSize,
                orderBy: 'created_at desc',
            })
            setOrderInventory(data)
            mapData(data?.orderInventory)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    const mapData = async (data: any[]) => {
        try {
            if (!data || data.length === 0) return
            const orderIds = data.map((item: any) => item.order_id)
            const resp = await apiGetSimInventoryByOrderIds({ orderIds })
            const simInventoryData = resp.data.simInventory
            setOrderInventory((prev: any) => ({
                ...prev,
                orderInventory: data.map((item: any) => ({
                    ...item,
                    simInventory: simInventoryData.filter((sim: any) => sim.order_id === item.order_id),
                }))
            }))
            setListSimByOrderIds(simInventoryData)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const fetchCompanies = useCallback(async () => {
        try {
            const { data } = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            const options = data.companies?.filter((item: ICompany) => item.supportChannels?.includes('INVENTORY')).map((item: ICompany) => ({
                label: item.name,
                value: item.code
            }))
            setCompanyOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies])

    const handleSearch = ({ cpCode, status }: any) => {
        if (cpCode || status) {
            setSearch({ cpCode, status })
            setCurrentPage(defaultParams.PAGE)
            setPageSize(defaultParams.LIMIT)
        }
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleChange = (id: any, key: any, value: any) => {
        setSimInventory((prev) => prev.map((cond) => (cond.id === id ? { ...cond, [key]: value } : cond)))
    }

    const validateSimInventory = () => {
        const isValid = simInventory.every((item) => {
            return (item.startSim?.length >= 19 && /^\d+$/.test(item.startSim))
                && (item.endSim?.length >= 19 && /^\d+$/.test(item.endSim))
                && (item.startSim?.length === item.endSim?.length)
                && item.startSim?.slice(0, -5) === item.endSim?.slice(0, -5)
                && Number(item.startSim?.slice(-5)) <= Number(item.endSim?.slice(-5))
        })
        if (!isValid) {
            toastError('Serial Sim không hợp lệ')
            return false
        }
        const lastSim = simInventory[simInventory.length - 1]
        lastSim.prefix_sim = lastSim.startSim.slice(0, 14)
        lastSim.start_sim = Number(lastSim.startSim.slice(-5))
        lastSim.end_sim = Number(lastSim.endSim.slice(-5))
        for (let i = 0; i < simInventory.length - 1; i++) {
            if (simInventory[i].prefix_sim === lastSim.prefix_sim
                && Number(simInventory[i].start_sim) <= Number(lastSim.end_sim)
                && Number(lastSim.start_sim) <= Number(simInventory[i].end_sim)
            ) {
                toastError('Serial Sim không được trùng nhau')
                return false
            }
        }
        return true
    }

    const handleAddSimInventory = () => {
        if (!validateSimInventory()) return
        setSimInventory((prev) => [...prev.map((item) => {
            item.disabled = true
            item.prefix_sim = item.startSim.slice(0, 14)
            item.start_sim = Number(item.startSim.slice(-5))
            item.end_sim = Number(item.endSim.slice(-5))
            item.total_sim = item.end_sim - item.start_sim + 1
            return item
        }), { ...initSimInventory, id: simInventory.length + 1 }])
    }

    const handleAddOrderInventory = async () => {
        try {
            setIsLoading(true)
            if (!orderInfo.cpCode || !orderInfo.description) {
                toastError('Vui lòng nhập đầy đủ thông tin')
                return
            }
            if (!validateSimInventory()) return
            const orderPayload = {
                cpCode: orderInfo.cpCode,
                description: orderInfo.description,
                simInventory,
            }
            if (selectedOutletId) {
                await apiCreateOutletOrder({
                    outletId: selectedOutletId,
                    order: orderPayload,
                    note: orderInfo.description,
                })
                toastSuccess('Đã tạo đơn + gán SIM vào đại lý')
            } else {
                await apiAddOrderInventory(orderPayload)
                toastSuccess('nạp sim đang thực hiện! Vui lòng kiểm tra kết quả sau ít phút')
            }
            fetchData()
            handleCloseModalAddOrderInventory()
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCloseModalAddOrderInventory = () => {
        setModalUpload(false)
        setOrderInfo(initOrderInfo)
        setSimInventory([initSimInventory])
        setOutletOptions([])
        setSelectedOutletId(undefined)
    }

    // Fetch outlets scoped to the selected cpCode. If cpCode has no outlets,
    // the "Đại lý (tùy chọn)" select stays empty and user proceeds as raw
    // Kho xuất order (no link).
    useEffect(() => {
        if (!orderInfo.cpCode) {
            setOutletOptions([])
            setSelectedOutletId(undefined)
            return
        }
        let mounted = true
        apiFilterOutlets({ cpCodes: [orderInfo.cpCode], limit: 500 }).then((resp: any) => {
            if (!mounted) return
            const list: IOutlet[] = resp?.data?.data ?? []
            setOutletOptions(list.map(o => ({
                label: `${o.name || '(không tên)'}${o.phone ? ' · ' + o.phone : ''}`,
                value: o.id,
            })))
        }).catch(() => {
            if (mounted) setOutletOptions([])
        })
        return () => { mounted = false }
    }, [orderInfo.cpCode])

    const handleOpenModalUpdate = async (data: any) => {
        formUpdate.setFieldsValue(data)
        setModalUpdate({ isOpen: true, data })
        setUpdateSelectedOutletId(undefined)
        setExistingLinkedOutletId(null)
        // Detect existing link: fetch sim_inv IDs of this order, then filter
        // outlet_inventory_link by those IDs. Any hit → order already linked.
        if (!data?.order_id) return
        try {
            const simResp: any = await apiGetSimInventoryByOrderIds({ orderIds: [data.order_id] })
            const sims: any[] = simResp?.data?.simInventory ?? []
            const simIds = sims.map(s => Number(s.id)).filter(n => n > 0)
            if (simIds.length === 0) return
            const linkResp: any = await apiFilterOutletLinks({ simInventoryIds: simIds, limit: 500 })
            const links: any[] = linkResp?.data?.data ?? []
            if (links.length > 0) {
                setExistingLinkedOutletId(links[0].outletId || null)
            }
        } catch (err) {
            console.log('detect existing link failed', err)
        }
    }

    const handleCloseModalUpdate = () => {
        setModalUpdate({ isOpen: false, data: null })
        formUpdate.resetFields()
        setUpdateOutletOptions([])
        setUpdateSelectedOutletId(undefined)
        setExistingLinkedOutletId(null)
    }

    // Update modal: fetch outlets when cpCode of the order changes.
    const updateCpCode = Form.useWatch('cpCode', formUpdate)
    useEffect(() => {
        if (!modalUpdate.isOpen || !updateCpCode) {
            setUpdateOutletOptions([])
            return
        }
        let mounted = true
        apiFilterOutlets({ cpCodes: [updateCpCode], limit: 500 }).then((resp: any) => {
            if (!mounted) return
            const list: IOutlet[] = resp?.data?.data ?? []
            setUpdateOutletOptions(list.map(o => ({
                label: `${o.name || '(không tên)'}${o.phone ? ' · ' + o.phone : ''}`,
                value: o.id,
            })))
        }).catch(() => {
            if (mounted) setUpdateOutletOptions([])
        })
        return () => { mounted = false }
    }, [modalUpdate.isOpen, updateCpCode])

    const handleUpdateOrderInventory = async (values: any) => {
        try {
            setIsLoading(true)
            await updateOrderInventory({ orderInventory: values })
            // Post-hoc link: only when the order has no existing link + user picked an outlet.
            if (!existingLinkedOutletId && updateSelectedOutletId && modalUpdate.data?.order_id) {
                try {
                    await apiLinkExistingOutletOrder({
                        outletId: updateSelectedOutletId,
                        orderId: modalUpdate.data.order_id,
                    })
                    toastSuccess('Đã cập nhật + gán SIM vào đại lý')
                } catch (linkErr) {
                    console.log('link existing failed', linkErr)
                    toastError('Cập nhật đơn OK nhưng gán đại lý thất bại — thử lại từ tab Kho SIM')
                }
            } else {
                toastSuccess('Cập nhật đơn hàng thành công!')
            }
            fetchData()
            handleCloseModalUpdate()
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra khi cập nhật!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='cpCode' label='Công ty'>
                    <Select
                        style={{ minWidth: 170 }}
                        showSearch
                        placeholder="Chọn công ty"
                        options={companyOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
                <Form.Item name='status' label='Trạng thái'>
                    <Select
                        style={{ width: 170 }}
                        showSearch
                        placeholder="Chọn trạng thái"
                        options={orderStatusOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
            </FormSearch>
            {account?.canAddOrderInventory && (
                <div style={{ textAlign: 'end', marginBottom: 20 }}>
                    <Button onClick={() => setModalUpload(true)} type='primary'>Thêm đơn hàng</Button>
                </div>
            )}
            <Descriptions
                style={{ maxWidth: 600 }}
                items={[
                    {
                        key: 'totalOrder',
                        label: 'Tổng đơn hiện thị',
                        children: orderInventory.orderInventory?.length || 0,
                    },
                    {
                        key: 'totalSuccess',
                        label: 'Tổng thành công',
                        children: orderInventory.orderInventory?.reduce((acc: number, curr: any) => acc + curr.total_success, 0) || 0,
                    },
                    {
                        key: 'totalActive',
                        label: 'Tổng đã kích',
                        children: listSimByOrderIds?.reduce((acc: number, curr: any) => acc + curr.total_active, 0) || 0,
                    },
                ]}
            />
            <CustomTable
                namePage='đơn hàng'
                columns={columns}
                dataSource={orderInventory.orderInventory}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={orderInventory.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal title='Thêm đơn hàng' open={modalUpload}
                onCancel={handleCloseModalAddOrderInventory}
                footer={[
                    <Button onClick={handleAddOrderInventory}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={handleCloseModalAddOrderInventory}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Form layout="vertical" autoComplete="off" style={{ marginBottom: 40 }}>
                    <Form.Item label='Công ty'>
                        <Select
                            style={{ maxWidth: 200 }}
                            value={orderInfo.cpCode}
                            onChange={value => setOrderInfo({ ...orderInfo, cpCode: value })}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    {outletOptions.length > 0 && (
                        <Form.Item
                            label='Đại lý (tùy chọn)'
                            tooltip='Nếu chọn, đơn hàng sẽ tự động gán SIM vào đại lý này (outlet_inventory_link). Bỏ trống nếu là đơn hàng thường.'
                        >
                            <Select
                                allowClear
                                style={{ maxWidth: 400 }}
                                value={selectedOutletId}
                                onChange={value => setSelectedOutletId(value)}
                                showSearch
                                placeholder='Chọn đại lý nhận SIM (bỏ qua nếu không cần)'
                                options={outletOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                    )}
                    {simInventory.map((sim) => (
                        <div key={sim.id} style={{ display: 'flex', gap: 20 }}>
                            <Form.Item style={{ flex: 1 }} label='Serial bắt đầu'>
                                <Input
                                    disabled={sim.disabled}
                                    value={sim.startSim}
                                    onChange={e => handleChange(sim.id, "startSim", e.target.value)}
                                />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} label='Serial kết thúc'>
                                <Input
                                    disabled={sim.disabled}
                                    value={sim.endSim}
                                    onChange={e => handleChange(sim.id, "endSim", e.target.value)}
                                />
                            </Form.Item>
                            {sim.total_sim > 0 && (
                                <Form.Item label=' '>
                                    <b>{sim.total_sim}</b>
                                </Form.Item>
                            )}
                            <Form.Item label=' '>
                                <Button
                                    onClick={handleAddSimInventory}
                                    icon={<PlusOutlined />} size='small'
                                />
                            </Form.Item>
                            <Form.Item label=' '>
                                {simInventory.length > 1 && (
                                    <Button
                                        onClick={() => setSimInventory((prev) => prev.filter((item) => item.id !== sim.id))}
                                        icon={<MinusOutlined />} danger size='small'
                                    />
                                )}
                            </Form.Item>
                        </div>
                    ))}
                    <Form.Item style={{ flex: 1 }} label='Mô tả'>
                        <Input.TextArea
                            value={orderInfo.description}
                            onChange={(e) => setOrderInfo({ ...orderInfo, description: e.target.value })}
                        />
                    </Form.Item>
                </Form>
                <p><b>Ghi chú:</b></p>
                <p>14 số đầu của dãy Serial phải trùng nhau</p>
                <p><b>Ví dụ:</b></p>
                <p>Serial bắt đầu: 8984048000900700000</p>
                <p>Serial kết thúc: 8984048000900799999</p>
                <Divider />
            </Modal>
            <Modal
                title='Cập nhật đơn hàng'
                open={modalUpdate.isOpen}
                onCancel={handleCloseModalUpdate}
                footer={[]}
                width={600}
            >
                <Form
                    form={formUpdate}
                    onFinish={handleUpdateOrderInventory}
                    layout="vertical"
                    autoComplete="off"
                    style={{ marginTop: 20 }}
                >
                    <Form.Item name='id' style={{ display: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item label='Mã đơn hàng' style={{ flex: 1 }}>
                            <Input
                                value={modalUpdate.data?.order_id}
                                disabled
                            />
                        </Form.Item>
                        <Form.Item label='Nhân sự' style={{ flex: 1 }}>
                            <Input
                                value={modalUpdate.data?.owner_by}
                                disabled
                            />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='cpCode' label='Công ty' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn công ty"
                                options={companyOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                        <Form.Item name='status' label='Trạng thái' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn trạng thái"
                                options={orderStatusOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='total_sim' label='Tổng Sim' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name='total_success' label='Tổng thành công' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name='total_fail' label='Tổng thất bại' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                    </div>
                    <Form.Item name='description' label='Mô tả'>
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item label='Đại lý (gán SIM)' tooltip='Nếu đơn chưa gán đại lý, chọn ở đây để tự động link toàn bộ SIM vào đại lý.'>
                        {existingLinkedOutletId ? (
                            <Input
                                disabled
                                value={updateOutletOptions.find(o => o.value === existingLinkedOutletId)?.label || existingLinkedOutletId}
                                suffix={<span style={{ color: '#888', fontSize: 12 }}>đã gán</span>}
                            />
                        ) : (
                            <Select
                                allowClear
                                value={updateSelectedOutletId}
                                onChange={v => setUpdateSelectedOutletId(v)}
                                showSearch
                                placeholder={updateOutletOptions.length > 0 ? 'Chọn đại lý để gán SIM (tùy chọn)' : 'Công ty chưa có đại lý'}
                                options={updateOutletOptions}
                                filterOption={filterOptionByLabel}
                                disabled={updateOutletOptions.length === 0}
                            />
                        )}
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            htmlType='submit'
                            style={{ background: '#ff9800', color: 'white' }}>
                            Cập nhật
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    )
}
